export type StreamFormat = 'hls' | 'dash';

/**
 * Detects the streaming format from a URL by checking for known patterns:
 * - File extensions: .m3u8, .mpd
 * - Path segments: /m3u8/, /mpd/ (common in CDN/proxy URLs)
 * - Iconik proxy container path segments: /hls/, /dash/
 */
export function getStreamFormat(url: string): StreamFormat | undefined {
  const lowerUrl = url.toLowerCase();
  if (
    lowerUrl.includes('.m3u8') ||
    lowerUrl.includes('/m3u8/') ||
    lowerUrl.endsWith('/m3u8') ||
    lowerUrl.includes('/hls/') ||
    lowerUrl.endsWith('/hls')
  ) {
    return 'hls';
  }

  if (
    lowerUrl.includes('.mpd') ||
    lowerUrl.includes('/mpd/') ||
    lowerUrl.endsWith('/mpd') ||
    lowerUrl.includes('/dash/') ||
    lowerUrl.endsWith('/dash')
  ) {
    return 'dash';
  }

  return undefined;
}

const streamFormatsByName: Record<string, StreamFormat> = {
  HLS: 'hls',
  M3U8: 'hls',
  DASH: 'dash',
  MPD: 'dash',
};

/**
 * Detects the streaming format from a proxy's format name. Search results carry
 * the format but not always a URL, so this is the only signal available there.
 */
export function getStreamFormatByName(
  formatName: string,
): StreamFormat | undefined {
  return streamFormatsByName[formatName.toUpperCase()];
}

/**
 * Detects the streaming format by inspecting manifest file contents:
 * - HLS manifests always begin with '#EXTM3U'
 * - DASH manifests are XML documents containing '<MPD'
 */
export function getStreamFormatByContent(
  content: string,
): StreamFormat | undefined {
  if (content.trimStart().startsWith('#EXTM3U')) {
    return 'hls';
  }
  if (content.includes('<MPD')) {
    return 'dash';
  }
  return undefined;
}
